import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import React, { useEffect, useMemo, useRef } from "react";
import Chart from "react-google-charts";
import {
    GoogleChartWrapper,
    GoogleViz,
    GoogleVizEventName,
} from "react-google-charts/dist/types";
import { getVAxisWidth, INSIDE_V_AXIS_CHART_AREA } from "utils/helpers";
import theme from "../../../Theme/AppTheme";
import { defaultColorScheme } from "./GraphColorSchemes";
import moment, { Moment } from "moment";
import { XValueType } from "utils/ReportDataToGoogleFormat";
import { useMediaQuery } from "@mui/material";
import { SignalsTemporalLabels } from "components/Signals/SignalsModels";
import {
    getCompactMonthLabel,
    getXDateRangeLabel,
} from "components/Signals/BaseObjects/ChartCard/dateBucketLabels";

type AxesScaleTypes = "log" | "mirrorLog" | null;
type GoogleDataFormat = any[][];

interface IndividualSeriesOptions {
    annotations?: Record<string, unknown>;
    color?: string;
    curveType?: string;
    labelInLegend?: string;
    lineDashStyle?: string;
    lineWidth?: number;
    pointShape?: string;
    pointSize?: number;
    pointsVisible?: boolean;
    targetAxisIndex?: number;
    visibleInLegend?: boolean;
}

type SeriesOptions =
    | Record<number, IndividualSeriesOptions>
    | IndividualSeriesOptions[];

// see: https://developers.google.com/chart/interactive/docs/customizing_axes#number-formats
type NumberFormatPreset =
    | "none"
    | "decimal"
    | "scientific"
    | "currency"
    | "percent"
    | "short"
    | "long";

interface OwnProps {
    data?: GoogleDataFormat;
    colorScheme?: string[];
    dataOpacity?: number;

    legendPosition: "none" | "bottom" | "in" | "right" | "top";
    legendAlignment?: "start" | "center" | "end";
    showGrid?: boolean;
    yTitle?: string;
    xTitle?: string;
    vAxisFormat?: NumberFormatPreset;

    onSelect?: (
        series: string | null,
        x: XValueType | null,
        y: string | number | null | any,
        chartId?: string,
        columnId?: string,
    ) => void;

    chartAreaLeftOffset?: number | string;
    chartAreaTopOffset?: number | string;
    chartAreaHeight?: number | string;
    chartAreaWidth?: number | string;
    slantedTextAngle?: number;
    verticalScaleType?: AxesScaleTypes;

    showDemoData?: boolean;
    asWidget?: boolean;
    asAreaChart?: boolean;
    fixedHeight?: boolean;
    vAxisFontSize?: string;
    hAxisFontSize?: string;
    vAxisTitleStyle?: Record<string, unknown>;
    vAxisTextStyle?: Record<string, unknown>;
    vAxisMaxValue?: number;
    /**
     * Draws the value axis's numbers inside the plot, so a narrow chart keeps
     * them without giving up plot width.
     */
    vAxisTextInside?: boolean;
    /**
     * Leaves the year out of date labels so they fit a narrow chart. Without
     * it, the year is left out whenever the window is narrower than the `lg`
     * breakpoint.
     */
    shortDateLabels?: boolean;

    onChartReady?: (
        chartWrapper: GoogleChartWrapper,
        google: GoogleViz,
    ) => void;

    metadata?: Map<string, any[]>; // deprecated
    chartId?: string;
    chartHeight?: string;
    hideDataLabels?: boolean;
    hideDataMarkers?: boolean;

    usesHtmlTooltips?: boolean;
    series?: SeriesOptions;
    disableSelection?: boolean;
    interpolateNulls?: boolean;
    temporalGrain?: SignalsTemporalLabels;
    startDate?: Moment;
    endDate?: Moment;
}

type Props = OwnProps;

const AcxLineGraph: React.FC<Props> = (props) => {
    const wrapper = useRef<null | any>();
    useEffect(() => {
        return () => {
            wrapper.current?.getChart()?.clearChart();
        };
    }, []);

    const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
    const shortDateLabels = props.shortDateLabels ?? isSmallScreen;

    const data = Boolean(props.showDemoData) ? fake_data : props.data;

    const showLegend = (data?.[0].length ?? 0) > 2;

    const options = useMemo(() => {
        // Google charts don't have a simple way to adjust padding
        // To do this we adjust the view window based on the data

        let datesFromData =
            data?.map((row) => row[0].f).filter((d) => d instanceof Date) ?? [];

        // Padding to the left and right of the lines is a fraction of the total time range,
        const firstDate = moment(datesFromData[0]);
        const lastDate = moment(datesFromData[datesFromData.length - 1]);
        const hoursBetweenDates = lastDate.diff(firstDate, "hours");
        const sidePadding = hoursBetweenDates * 0.01;

        let momentFormatString = "MMM D";
        const shouldShowYear =
            props.startDate?.year() !== props.endDate?.year() &&
            !shortDateLabels;
        if (shouldShowYear) {
            momentFormatString = "MMM D, YYYY";
        }
        const dateTicks = datesFromData.map((d) => {
            // A month's full span wraps onto several lines on a narrow chart.
            const dateRangeLabel =
                shortDateLabels && props.temporalGrain === "Monthly"
                    ? getCompactMonthLabel(
                          moment(d),
                          props.startDate,
                          props.endDate,
                      )
                    : getXDateRangeLabel(
                          moment(d),
                          props.temporalGrain ?? "Daily",
                          props.startDate,
                          props.endDate,
                          momentFormatString,
                      );

            return {
                v: d,
                f: dateRangeLabel,
            };
        });

        const shouldAddPaddingBetweenLegendAndXValues =
            shouldShowYear && props.legendPosition !== "none";

        const maxValue = props.vAxisMaxValue ?? 0;

        const res = {
            interpolateNulls: props.interpolateNulls ?? true,
            fontName: "Inter",
            dataOpacity: props.dataOpacity ?? 0.65,
            animation: {
                startup: true,
                duration: 500,
                easing: "out",
            },
            colors: props.colorScheme ?? defaultColorScheme,
            tooltip: {
                textStyle: { fontName: "Inter" },
                isHtml: !!props.usesHtmlTooltips,
            },
            legend: {
                position: showLegend ? props.legendPosition : "none",
                alignment: props.legendAlignment ?? "start",
                textStyle: {
                    fontName: "Inter",
                },
                maxLines: 5,
            },
            chart: {},
            chartArea: {
                top: props.chartAreaTopOffset ?? "5%",
                height: props.chartAreaHeight
                    ? `${props.chartAreaHeight.toString().replace("%", "")}%`
                    : props.asWidget
                    ? "77%"
                    : "90%",
                ...(props.vAxisTextInside
                    ? INSIDE_V_AXIS_CHART_AREA
                    : {
                          left: props.vAxisFontSize
                              ? getVAxisWidth(props.vAxisFontSize)
                              : props.chartAreaLeftOffset ??
                                (props.asWidget ? "8%" : "4%"),
                          width:
                              props.chartAreaWidth ??
                              (props.asWidget ? "88%" : "90%"),
                          right: datesFromData.length ? "4%" : "none",
                      }),
                bottom:
                    // this increases the padding between the legend and x-axis labels
                    shouldAddPaddingBetweenLegendAndXValues ? 100 : undefined,
            },
            hAxis: {
                title: props.xTitle,
                titleTextStyle: {
                    color: theme.palette.gray[400],
                    fontSize: props.asWidget ? 13 : 16,
                    fontName: "Inter Bold",
                },
                textStyle: {
                    color: theme.palette.grey[500],
                    fontSize: props.asWidget ? 10 : props.hAxisFontSize ?? 9,
                    fontName: "Inter Bold",
                },
                gridlines: {
                    color:
                        props.showGrid && !datesFromData.length
                            ? "E7E7E7"
                            : "none",
                },
                showTextEvery: 1, // prevent automatically hiding some x-axis labels if limited space
                ...(datesFromData.length
                    ? {
                          ticks: dateTicks as any, // RGC expects a (number | Date)[] here but we need to override this for date range display
                          viewWindowMode: "explicit",
                          viewWindow: {
                              // Add left padding by extending the window slightly below the minimum value
                              min: firstDate
                                  .subtract(sidePadding, "hours")
                                  .toDate(),
                              // Add right padding by extending the window slightly above the maximum value
                              max: lastDate.add(sidePadding, "hours").toDate(),
                          },
                      }
                    : {}),
            },
            pointSize: props.hideDataMarkers ? 0 : 5,
            vAxis: {
                scaleType: null as AxesScaleTypes,
                title: props.yTitle,
                textPosition: props.vAxisTextInside ? "in" : "out",
                titleTextStyle: {
                    color: theme.palette.gray[400],
                    italic: false,
                    fontSize: props.asWidget ? 13 : 16,
                    fontName: "Inter Bold",
                    ...props.vAxisTitleStyle,
                },
                textStyle: {
                    color: theme.palette.gray[400],
                    ...props.vAxisTextStyle,
                    fontSize: props.asWidget ? 10 : props.vAxisFontSize ?? 13,
                    fontName: "Inter Bold",
                },
                gridlines: {
                    color: props.showGrid ? "E7E7E7" : "none",
                },
                minValue: 0,
                maxValue,
                format: props.vAxisFormat,
                viewWindow: {
                    // Add padding between graph and labels by extending window slightly beyond minimum value
                    min: 0 - maxValue * 0.04,
                },
            },
            annotations: props.hideDataLabels
                ? {
                      textStyle: {
                          fontSize: 0, // Set font size to 0 to hide labels
                      },
                  }
                : { textStyle: { color: "black" } },

            series: props.series,
            enableInteractivity: true,
        };
        res.vAxis.scaleType = props.verticalScaleType ?? null;

        return res;
    }, [
        props.asWidget,
        props.chartAreaHeight,
        props.chartAreaLeftOffset,
        props.chartAreaTopOffset,
        props.chartAreaWidth,
        props.colorScheme,
        props.dataOpacity,
        props.hAxisFontSize,
        props.hideDataLabels,
        props.hideDataMarkers,
        props.legendAlignment,
        props.legendPosition,
        props.showGrid,
        props.vAxisFontSize,
        props.verticalScaleType,
        props.xTitle,
        props.yTitle,
        props.usesHtmlTooltips,
        props.vAxisTitleStyle,
        props.vAxisTextStyle,
        props.vAxisFormat,
        props.series,
        props.interpolateNulls,
        props.temporalGrain,
        props.startDate,
        props.endDate,
        props.vAxisMaxValue,
        props.vAxisTextInside,
        showLegend,
        data,
        shortDateLabels,
    ]);

    // chartEvents callbacks are memoized and can only access props from initial render
    // this ref stores an updated data prop for use in the chartEvent select callback
    const dataRef = useRef({ data: props.data });

    useEffect(() => {
        dataRef.current = { data: props.data };
    }, [props.data]);

    const standardChartSelectionEvent = [
        {
            eventName: "select" as GoogleVizEventName,
            callback: ({ chartWrapper }) => {
                const chart = chartWrapper.getChart();
                const selection = chart.getSelection();
                if (selection.length === 1) {
                    const [selectedItem] = selection;
                    const dataTable = chartWrapper.getDataTable();
                    const { row, column } = selectedItem;

                    if (row !== null && column !== null) {
                        const rowLabel = dataRef.current.data?.[row + 1]?.[0];

                        props.onSelect?.(
                            dataTable?.getColumnLabel(column) ?? null,
                            rowLabel,
                            dataTable?.getValue(row, column) ?? null,
                            props.chartId,
                            dataTable?.getColumnId(column),
                        );
                    } else if (row !== null) {
                        const rowLabel = dataRef.current.data?.[row + 1]?.[0];

                        props.onSelect?.(
                            null,
                            rowLabel,
                            null,
                            props.chartId,
                            dataTable?.getColumnId(column),
                        );
                    } else if (column !== null) {
                        props.onSelect?.(
                            dataTable?.getColumnLabel(column) ?? null,
                            null,
                            null,
                        );
                    }
                }
            },
        },
    ];

    const disabledChartSelectionEvent = [
        {
            eventName: "select" as GoogleVizEventName,
            callback: ({ chartWrapper }) => {
                const chart = chartWrapper.getChart();
                chart.setSelection([]); // clears the selection immediately
            },
        },
    ];

    return (
        <>
            {Boolean(data) && (
                <Chart
                    width={`100%`}
                    height={
                        props.chartHeight
                            ? props.chartHeight
                            : props.asWidget
                            ? props.fixedHeight
                                ? "300px"
                                : "97%"
                            : "100%"
                    }
                    chartType={props.asAreaChart ? "AreaChart" : "LineChart"}
                    loader={
                        <AcxLoadingIndicator
                            color="secondary"
                            alternate="PuffLoader"
                            size={96}
                            style={
                                props.chartHeight
                                    ? {
                                          height: props.chartHeight,
                                          zIndex: theme.zIndex.appBar - 2,
                                      }
                                    : {}
                            }
                        />
                    }
                    data={data}
                    options={options}
                    getChartWrapper={(w, g) => {
                        wrapper.current = w;
                        props.onChartReady?.(w, g);
                    }}
                    chartEvents={
                        props.disableSelection
                            ? disabledChartSelectionEvent
                            : standardChartSelectionEvent
                    }
                />
            )}
        </>
    );
};

const fake_data = [
    ["x", "Insights", "Quality", "Compliance"],
    [0, 0, 0, 2],
    [1, 10, 5, 12],
    [2, 23, 15, 34],
    [3, 17, 9, 5],
    [4, 18, 10, 22],
    [5, 9, 5, 3],
    [6, 11, 3, 3],
    [7, 27, 19, 50],
];

export default AcxLineGraph;
